import { AnalyticsClient } from './analytics/client';
import { DiagnosticUploader } from './analytics/diagnostic-uploader';
import { loadAnalyticsIdentity } from './analytics/identity';
import { SessionTracker } from './analytics/session-tracker';
import { GameAudio } from './audio/audio';
import { LobbyController } from './app/controllers/lobby-controller';
import { MatchController } from './app/controllers/match-controller';
import { NetworkSessionController } from './app/controllers/network-session-controller';
import { NetworkPresentationController } from './app/controllers/network-presentation-controller';
import { ResultController } from './app/controllers/result-controller';
import { SessionController } from './app/controllers/session-controller';
import { SettingsController } from './app/controllers/settings-controller';
import { ToastController } from './app/controllers/toast-controller';
import { requireElement } from './app/dom';
import { MatchFinalizer } from './app/match-finalizer';
import { parseSavedSettings } from './app/settings';
import { clearStartupRecovery, consumeStartupRetry } from './app/startup-recovery';
import { createGameContent, DEFAULT_BOT_DECK, DEFAULT_PLAYER_DECK } from './content/catalog';
import { ENGINE_VERSION } from './engine/engine';
import { I18nService, resolveLocale } from './i18n/i18n-service';
import { loadLatestDiagnosticBundle, recoverInterruptedDiagnosticBundle } from './storage/diagnostics-store';

const content = createGameContent();
const fastMode = new URLSearchParams(location.search).has('fast');
const settings = parseSavedSettings(localStorage.getItem('towerarena.com.settings'));
const i18n = new I18nService(resolveLocale(settings.locale, navigator.languages));
const toast = new ToastController();
const identity = loadAnalyticsIdentity(localStorage, sessionStorage);
const analytics = new AnalyticsClient(identity, () => settings.shareAnonymousMetrics);
const tracker = new SessionTracker({ identity, engineVersion: ENGINE_VERSION, contentVersion: content.version, visible: !document.hidden, storage: sessionStorage });
const uploader = new DiagnosticUploader(identity, () => settings.uploadDiagnosticLogs);
const audio = new GameAudio(settings);
const defaultDecks = { player: DEFAULT_PLAYER_DECK, bot: DEFAULT_BOT_DECK } as const;
const lobby = new LobbyController(content, defaultDecks, i18n, analytics, toast);
const networkPresentation = new NetworkPresentationController();

let session!: SessionController;
const result = new ResultController({
  i18n,
  analytics,
  toast,
  onAgain: () => session.again(),
  onReplay: (bundle) => session.replay(bundle),
  onHome: () => session.showMenu(),
});
const match = new MatchController({
  content,
  i18n,
  audio,
  toast,
  fastMode,
  onFinished: (runtime) => { void session.finish(runtime); },
  onPauseChanged: (paused) => tracker.setMode(paused ? 'paused' : 'gameplay'),
});
const finalizer = new MatchFinalizer({ content, settings, analyticsIdentity: identity, analytics, sessionTracker: tracker, diagnosticUploader: uploader, audio, i18n, lobby, result, toast });
session = new SessionController({ content, settings, i18n, lobby, match, result, finalizer, toast, analytics, sessionTracker: tracker, networkPresentation, fastMode });
const network = new NetworkSessionController({ content, i18n, lobby, toast, analytics, fastMode, onReady: (connection) => { void session.startNetwork(connection); } });

let resumeAfterSettings = false;
const settingsController = new SettingsController({
  settings,
  i18n,
  audio,
  analytics,
  analyticsIdentity: identity,
  sessionTracker: tracker,
  diagnosticUploader: uploader,
  toast,
  setRendererQuality: (quality) => match.setQuality(quality),
  onLocalLogsCleared: () => result.clear(),
  onClosed: () => {
    if (resumeAfterSettings && match.paused) match.togglePause();
    resumeAfterSettings = false;
  },
});

function openSettings(): void {
  resumeAfterSettings = match.active && !match.paused && match.state.phase === 'running' && match.state.context.mode === 'bot';
  if (resumeAfterSettings) match.togglePause();
  settingsController.open();
}

async function boot(): Promise<void> {
  const version = requireElement<HTMLElement>('#versionLabel');
  version.textContent = i18n.t('app.engineVersion', { engineVersion: ENGINE_VERSION, count: content.cards.size });
  version.dataset.engineVersion = ENGINE_VERSION;
  version.dataset.contentVersion = content.version;
  i18n.applyDocument();
  i18n.subscribe(() => {
    i18n.applyDocument();
    version.textContent = i18n.t('app.engineVersion', { engineVersion: ENGINE_VERSION, count: content.cards.size });
  });
  audio.preload();
  settingsController.persist();
  await lobby.boot();
  try {
    await recoverInterruptedDiagnosticBundle(settings.uploadDiagnosticLogs);
    const bundle = await loadLatestDiagnosticBundle();
    if (bundle) result.setLastBundle(bundle, bundle.checkpoints.at(-1)?.checksum ?? null);
  } catch (error) {
    result.clear();
    toast.show(i18n.t('toast.logSaveFailed', { error: String(error) }));
  }
  network.boot();
  analytics.event('app_loaded', { engineVersion: ENGINE_VERSION, contentVersion: content.version, language: i18n.locale });
  analytics.session(tracker.snapshot());
  void uploader.process();
  const retry = consumeStartupRetry(sessionStorage);
  if (retry === 'live') void session.startBot();
  else if (retry === 'replay') { clearStartupRecovery(sessionStorage); toast.show(i18n.t('toast.appUpdated'), 6); }
}

requireElement<HTMLButtonElement>('#settingsButton').addEventListener('click', openSettings);
requireElement<HTMLButtonElement>('#startSettingsButton').addEventListener('click', openSettings);
document.addEventListener('pointerdown', () => tracker.noteInteraction(), { passive: true });
document.addEventListener('keydown', () => tracker.noteInteraction(), { passive: true });
document.addEventListener('visibilitychange', () => session.visibilityChanged(document.hidden));
window.addEventListener('online', () => { void uploader.process(); });
window.addEventListener('pagehide', () => session.reportPageHide());
window.addEventListener('error', (event) => { analytics.event('client_error', { message: event.message.slice(0, 300) }); session.reportFatalError('uncaught-error', event.error ?? event.message); });
window.addEventListener('unhandledrejection', (event) => { analytics.event('client_error', { message: String(event.reason).slice(0, 300) }); session.reportFatalError('unhandled-rejection', event.reason); });
window.setInterval(() => analytics.session(tracker.snapshot()), 5 * 60_000);
void boot();
